/**
 * @file This file is used for manual tests and will only be used temporarily.
 * It is needed while the generic schema handling solution for v2 List Report is still in development but
 * the previous logic is actually used at runtime.
 * For this purpose the file exposes a handler class that is capable to perform the three important tasks provided to the PageMap by specification
 * (generation of page specific schema, import, and export).
 * The implementation of these three tasks is forwarded to the corresponding 'classical' implementation files.
 * For manual tests you can set useGenericSchemaHandling to true. Then these three tasks will be executed based on the schema class ListReportNew
 * and the generic schema handling logic.
 * Do not forget to set the variable back to false, before submitting any changes!
 *
 * Note: This file also exposes some of its helper functions so that they can also be used by the unit tests that test the new schema generation logic for v2 LR as well.
 */

const useGenericSchemaHandling: boolean = false; // Set this to true temporarily for manual tests to use the generic schema handling logic.

import {
    type AccessorPath,
    type ChangeDefinitionInCreation,
    ChangeIndicator,
    FlexChangeLayer,
    type Manifest,
    ManifestSection,
    type ExportListReportV2Parameters,
    type ExportListReportWithPathV2Parameters,
    type ExportResults,
    type GenerateAppSchemaParameters,
    type ImportListReportV2Parameters,
    type PageConfig,
    type UI5Version,
    type ConversionExit,
    type ConversionExitProvider
} from '@sap/ux-specification-types';
import { FE_TEMPLATE_V2_LIST_REPORT, SAPUI5_VIEW_EXTENSION_LIST_REPORT } from '@sap/ux-specification-types/src/v2';
import type { Definition } from 'typescript-json-schema';
// Importing the 'classic' implementation of the three tasks:
import { generateListReportSchemaV2 as generateListReportSchemaV2Classic } from '../../generate/listReport';
import { createListReportConfig as createListReportConfigClassic } from '../../import/pages/listReport';
import { exportListReportPage as exportListReportPageClassic } from '../../export/exportPage';
import {
    type GenericSchemaInfoProvider,
    getAdaptedSchema,
    type SpecificSchemaGenerationInfo
} from '../../generate/schemaAdaptation';
import { ListReportNew } from './ListReportNew';
import { CREATE_WITH_PARAMETER_DIALOG } from '../controls/Table';
import { join } from 'path';
import { readFileSync } from 'fs';
import { getManifestPropertyByPath } from '../../utils';
import { getConfigForPage, type ImportConfigInfo, type PropertyTypeInfo } from '../../import/importPage';
import { exportPage, type ExportPageResults, type FlexInfo } from '../../export/exportPageGeneric';
import { exportToFlexChange } from '../../../common';
import { filterFlexChanges } from '../../import';

/**
 * An instance of this type provides access to the three main services (schema generation, import, and export) for a v2 List Report Page.
 *
 * @property generateListReportSchemaV2 - see generateListReportSchemaV2 in generate/listReport.ts for the purpose of this function
 * @property createListReportConfig - see createListReportConfig in import/pages/listReport.ts for the purpose of this function
 * @property exportListReportPage - see exportListReportPage in export/exportPage.ts for the purpose of this function
 */
type PageHandlerLR = {
    generateListReportSchemaV2: (generateParameters: GenerateAppSchemaParameters, genericSchema: Definition) => object;
    createListReportConfig: (importParameters: ImportListReportV2Parameters) => PageConfig | undefined;
    exportListReportPage: (
        exportParameters: ExportListReportV2Parameters | ExportListReportWithPathV2Parameters,
        ui5Version: UI5Version,
        deletionRequest: boolean
    ) => ExportResults;
};

/**
 * The instance of type PageHandlerLR to be used in productive environment (until generic logic has been completely implemented).
 */
const pageHandlerLRClassic = {
    generateListReportSchemaV2(generateParameters: GenerateAppSchemaParameters, genericSchema: Definition): object {
        return generateListReportSchemaV2Classic(generateParameters, genericSchema);
    },
    createListReportConfig(importParameters: ImportListReportV2Parameters): PageConfig | undefined {
        return createListReportConfigClassic(importParameters);
    },
    exportListReportPage(
        exportParameters: ExportListReportV2Parameters | ExportListReportWithPathV2Parameters,
        ui5Version: UI5Version,
        deletionRequest: boolean
    ): ExportResults {
        return exportListReportPageClassic(exportParameters, ui5Version, deletionRequest);
    }
};

let pageHandlerLRGeneric: PageHandlerLR; // Cached instance of the PageHandler using generic schema handling logic. Created on demand only.

/**
 * Gives access to the new version of the generic schema for a v2 List Report Page as it has been persisted by the build process.
 *
 * @returns the schema definition of the v2 List Report Page
 */
export function getPersistentListReportNewSchema(): Definition {
    // Access the generic schema for ListReportNew from the file system.
    let pathToDist: string; // path to the dist folder
    if (__dirname.endsWith('dist')) {
        //runtime case -> we have the dist folder already
        pathToDist = __dirname;
    } else {
        // unit test case -> need to navigate from the source older to the dist folder
        const endPosition = __dirname.lastIndexOf('specification');
        pathToDist = join(__dirname.substring(0, endPosition + 'specification'.length), 'dist');
    }

    const schemaPath = join(pathToDist, 'schemas', 'v2', 'ListReportNewConfig.json');
    const ListReportNewSchema = JSON.parse(readFileSync(schemaPath, 'utf8')) as Definition;
    return ListReportNewSchema;
}

/**
 * Collection of conversion exits needed in the import and export process of a v2 List Report Page.
 *
 * @param conversionExitName - the name of the conversion exit as passed to addConversionExit when the conversion exit was registered in the app specific schema.
 * @returns the implementation of the conversion exit
 */
const conversionExitProviderLR: ConversionExitProvider = function (
    conversionExitName: string
): ConversionExit | undefined {
    if (conversionExitName === 'createWithParameterDialog') {
        return CREATE_WITH_PARAMETER_DIALOG;
    }
    return undefined;
};

/**
 * Creates a GenericSchemaInfoProvider that can be used to access the required schema information for a v2 List Report Page when the generic schema handling logic is used.
 * Note that this function is called in two scenarios:
 * 1. In runtime case it is called when useGenericSchemaHandling has been set to true and the Page Editor for a v2 List Report page is opened.
 * 2. Unit tests for the generic schema handling logic of v2 List Report call this via function getGenericPageInfoProvider4NewPages.
 * Depending on the scenario the current directory (__dirname) will either point to the dist folder or to the source folder of this file.
 *
 * @returns a GenericSchemaInfoProvider that gives access to the schema information for the new v2 list report page.
 */
export function createGenericSchemaInfoProvider(): GenericSchemaInfoProvider {
    const ListReportNewSchema = getPersistentListReportNewSchema();
    return function (componentName: string): SpecificSchemaGenerationInfo | undefined {
        return componentName === FE_TEMPLATE_V2_LIST_REPORT
            ? {
                  genericSchema: ListReportNewSchema,
                  syncRuleProvider: ListReportNew
              }
            : undefined; // this instance is currently only working for v2 List Report
    };
}

/**
 * This function provides the configuration information for a v2 List Report Page.
 * All other page types are currently not handled by this function.
 *
 * @param componentName - name of the template component describing the page. Should identify the v2 list report page.
 * @returns the instance provider needed to create instances of the types for the v2 List Report Page, resp. undefined when another page is specified
 */
export function getConfigInfo(componentName: string): ImportConfigInfo | undefined {
    return componentName === FE_TEMPLATE_V2_LIST_REPORT
        ? {
              propertyInstanceProvider(propertyTypePath: PropertyTypeInfo[]): object {
                  return new ListReportNew();
              },
              conversionExitProvider: conversionExitProviderLR
          }
        : undefined;
}

/**
 * Provides the manifest path to the root page (either LR or ALP) the manifest of a FE v2 app.
 
 * @param manifest the manifest of the FE v2 app
 * @returns the manifest path to the root page resp. undefined if no root page is defined in the manifest
 */
export function getPathForRootPage(manifest: Manifest): AccessorPath | undefined {
    const { value: pagesSection } = getManifestPropertyByPath(manifest, [ManifestSection.generic], 'pages');
    if (Array.isArray(pagesSection)) {
        return [ManifestSection.generic, 'pages', 0];
    }
    return pagesSection && typeof pagesSection === 'object'
        ? [ManifestSection.generic, 'pages', Object.keys(pagesSection)[0]]
        : undefined;
}

/**
 * Conversion form the FlexInfo representation of a flex change into its string representation.
 *
 * @param ui5Version - specifies the ui5 version the change is written for
 * @param manifest - manifest of the app the change is created for
 * @param flexInfo - description of the change to be converted
 * @returns - string representation of the flex change
 */
export function flexInfoToChangeString(ui5Version: UI5Version, manifest: Manifest, flexInfo: FlexInfo): string {
    const isCustomer = !ui5Version.layer || ui5Version.layer === FlexChangeLayer.Customer;

    const flexChange: ChangeDefinitionInCreation = {
        controlId: flexInfo.controlId,
        controlType: flexInfo.controlType,
        content: {
            property: flexInfo.propertyName
        },
        sapui5Version: ui5Version.ui5Version,
        isCustomer
    };
    if (flexInfo.isBinding) {
        flexChange.content.newBinding = flexInfo.newValue as string;
        flexChange.type = 'propertyBindingChange';
    } else {
        flexChange.content.newValue = flexInfo.newValue;
    }
    return exportToFlexChange(flexChange, manifest);
}

/**
 * This function is called on demand. It creates an instance of type PageHandlerLR that uses the generic schema handling logic.
 *
 * @returns the created instance of type PageHandlerLR
 */
function createPageHandlerLRGeneric(): PageHandlerLR {
    // As the signatures of the functions provided by generic schema handling do not exactly fit to the signatures defined in type PageHandlerLR
    // (they are very similar to the classical ones) we need to build some adapters.

    const genericSchemaInfoProvider = createGenericSchemaInfoProvider();

    /* Translate the logical flex changes contained in exportPageResults into the string representation of the corresponding flex changes */
    const getFlexChangesAsStrings = function (
        appId: string,
        exportPageResults: ExportPageResults,
        ui5Version: UI5Version
    ): string[] {
        const localManifest = structuredClone(exportPageResults.manifest);
        localManifest[ManifestSection.app].id = appId;
        return exportPageResults.flexList.map(flexInfoToChangeString.bind(null, ui5Version, localManifest));
    };
    // Provide an instance that implements the PageHandlerLR interface using the generic schema handling logic:
    return {
        generateListReportSchemaV2(generateParameters: GenerateAppSchemaParameters): object {
            const manifest = generateParameters.manifest;
            const pagePath = getPathForRootPage(manifest);
            if (!pagePath) {
                return null;
            }
            const pageSpec = {
                appId: manifest[ManifestSection.app]?.id || '', // todo: do we need to care for variable appIds in the manifest (e.g. '${project.artifactId})?
                manifest,
                flex: generateParameters.flex || [],
                pagePath,
                annotations: [],
                parsedAnnotations: generateParameters.serviceAVT,
                fragments: generateParameters.fragments
            };
            return getAdaptedSchema(pageSpec, genericSchemaInfoProvider, generateParameters.logger);
        },
        createListReportConfig(importParameters: ImportListReportV2Parameters): PageConfig | undefined {
            const pagePath = getPathForRootPage(importParameters.manifest);
            return getConfigForPage(importParameters, pagePath, getConfigInfo);
        },
        exportListReportPage(
            exportParameters: ExportListReportV2Parameters | ExportListReportWithPathV2Parameters,
            ui5Version: UI5Version,
            deletionRequest: boolean
        ): ExportResults {
            const fragmentResults = exportParameters.fragments
                ? exportParameters.fragments.map(function (fragment) {
                      return {
                          ...fragment,
                          changeIndicator: ChangeIndicator.NoChange
                      };
                  })
                : [];
            const viewResults = exportParameters.views
                ? exportParameters.views.map(function (view) {
                      return {
                          ...view,
                          changeIndicator: ChangeIndicator.NoChange
                      };
                  })
                : [];
            const inParameters = {
                appId: exportParameters.appId,
                config: exportParameters.page.config,
                manifest: exportParameters.manifest,
                jsonSchema: exportParameters.jsonSchema,
                conversionExitProvider: conversionExitProviderLR,
                fragments: fragmentResults,
                views: viewResults,
                logger: exportParameters.logger
            };
            const exportPageResults = exportPage(inParameters);
            const flexChanges = getFlexChangesAsStrings(exportParameters.appId, exportPageResults, ui5Version);
            return {
                manifest: exportPageResults.manifest,
                fragments: exportPageResults.fragments,
                flexChanges,
                manifestChangeIndicator: exportPageResults.manifestChangeIndicator,
                views: exportPageResults.views
            };
        }
    };
}

/**
 * This function provides then instance of type PageHandlerLR that is to be used in productive environment.
 *
 * @returns the instance to be used
 */
function getPageHandlerLR(): PageHandlerLR {
    if (useGenericSchemaHandling) {
        pageHandlerLRGeneric = pageHandlerLRGeneric || createPageHandlerLRGeneric();
        return pageHandlerLRGeneric;
    }
    return pageHandlerLRClassic;
}

/**
 * Generates an app specific schema for the FE V2 ListReport from the generic schema.
 * Generic types are replaced by information from the app specific annotations.
 *
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 * @param {object} genericSchema - generic JSON schema of a list report
 * @returns appSchema - the application specific JSON schema
 */
export function generateListReportSchemaV2(
    generateParameters: GenerateAppSchemaParameters,
    genericSchema: Definition
): object {
    // Ensure that only those flex changes are used that refer to the LR
    generateParameters = {
        ...generateParameters,
        flex:
            generateParameters.entitySet && generateParameters.flex
                ? filterFlexChanges(
                      generateParameters.flex,
                      SAPUI5_VIEW_EXTENSION_LIST_REPORT,
                      generateParameters.entitySet.name
                  )
                : []
    };

    return getPageHandlerLR().generateListReportSchemaV2(generateParameters, genericSchema);
}

/**
 * Creates the configuration file content for a list report V2.
 *
 * @param importParameters  - object comprising all input data
 * @returns {v2.ListReportConfigV2 | undefined} - the configuration (JSON) for the list report
 */
export function createListReportConfig(importParameters: ImportListReportV2Parameters): PageConfig | undefined {
    return getPageHandlerLR().createListReportConfig(importParameters);
}

/**
 * Run through the given ListReport config and return respective manifest entry and flex changes.
 *
 * @param {ExportListReportV2Parameters} exportParameters - all API parameters needed for the export
 * @param ui5Version - SAP UI5 version
 * @param deletionRequest - if set to true, any manifest setting specified by entityPath gets deleted even if it comprises any unknown property
 * @returns ExportResults - The export result comprises the enhanced manifest as well as a list of flex changes.
 */
export function exportListReportPage(
    exportParameters: ExportListReportV2Parameters | ExportListReportWithPathV2Parameters,
    ui5Version: UI5Version,
    deletionRequest: boolean = false
): ExportResults {
    return getPageHandlerLR().exportListReportPage(exportParameters, ui5Version, deletionRequest);
}
